import {
    ProductAttribute,
    ProductInterface,
} from "../interfaces/ProductInterface";
import { ProductDetailsDataInterface } from "../interfaces/ProductDetailsDataInterface";
import { CategoryInterface } from "../interfaces/CategoryInterface";
import { FilterListInterface } from "../interfaces/FilterListInterface";
import { BrandingInterface } from "../interfaces/BrandingInterface";
import {
    DigitalSalesApiAttribute,
    DigitalSalesApiBranding,
    DigitalSalesApiCategoryNode,
    DigitalSalesApiFilter,
    DigitalSalesApiProductEntry,
} from "../interfaces/DigitalSalesApiInterface";

// The apiV2/digital-sales/product endpoint already resolves attribute
// values to display-ready strings server-side, so there is no raw "type"
// to branch on anymore. Attributes are mapped as pre-resolved "text" values.
const mapAttribute = (
    attribute: DigitalSalesApiAttribute,
): ProductAttribute => ({
    code: attribute.code,
    type: "text",
    localizable: false,
    label: attribute.label,
    value: attribute.value,
    is_bullet_points: attribute.is_bullet_points,
    items: attribute.items,
});

const flattenCategoryIds = (nodes: DigitalSalesApiCategoryNode[]): number[] =>
    nodes.flatMap((node) => [node.id, ...flattenCategoryIds(node.children)]);

const mapBeveragesLabel = (
    beverages: DigitalSalesApiProductEntry["detail"]["beverages"],
): string => (beverages ?? []).map((beverage) => beverage.label).join(", ");

const mapBeverageItems = (
    beverages: DigitalSalesApiProductEntry["detail"]["beverages"],
): NonNullable<DigitalSalesApiProductEntry["detail"]["beverages"]> =>
    (beverages ?? []).map((beverage) => ({
        code: beverage.code,
        label: beverage.label,
        icon_url: beverage.icon_url,
    }));

/**
 * Merges the split `index` (list) and `detail` (full product) payloads from
 * apiV2/digital-sales/product into a single flat product record, matching
 * the shape the rest of the app (IndexedDB storage, list rendering, product
 * detail rendering) already expects from ProductInterface /
 * ProductDetailsDataInterface.
 *
 * Fields not exposed by apiV2/digital-sales (productAssociations,
 * similarProductsSpecification, prices, canisterVolume, waterSupply,
 * productDownloads, marketingDownloads, olderModelAssociations) are not
 * present in this API and are defaulted so consumers degrade gracefully
 * instead of throwing.
 */
export const mapDigitalSalesProductEntry = (
    entry: DigitalSalesApiProductEntry,
): ProductInterface & ProductDetailsDataInterface => {
    const { index, detail } = entry;
    const categoryIds = flattenCategoryIds(detail.categories ?? []);

    return {
        id: detail.id,
        identifier: detail.identifier,
        description: detail.description?.value ?? "",
        categories: categoryIds,
        beverages: mapBeveragesLabel(detail.beverages),
        beverageItems: mapBeverageItems(detail.beverages),
        canisterVolume: "",
        dimensions: detail.dimensions ?? "",
        productAttributes: (index.productAttributes ?? []).map(mapAttribute),
        detailAttributes: (detail.productAttributes ?? []).map(mapAttribute),
        filterAttributes: (index.filterAttributes ?? []).map(mapAttribute),
        waterSupply: false,
        label: detail.label ?? index.label,
        productFamily: detail.productFamily
            ? {
                  id: detail.productFamily.id,
                  code: detail.productFamily.code,
                  label: detail.productFamily.label,
                  nameAttribute: detail.productFamily.nameAttribute,
              }
            : { id: 0, code: "", label: "", nameAttribute: "" },
        productState: detail.productState ?? "",
        prices: [],
        productImages: detail.productImages ?? [],
        price: 0,
        connection: index.connection ?? "",
        productDownloads: [],
        marketingDownloads: {},
        marketingDownloadNavItems: [],
        technicalDownloads: {},
        technicalDownloadNavItems: [],
        downloadsCount: { marketing: 0, technical: 0 },
        productVideos: [],
        productAssociations: {
            related: [],
            pack: [],
            cross_sell: [],
            spare_part: [],
            up_sell: [],
            previous_model: [],
        },
        olderModelAssociations: [],
    };
};

export const mapDigitalSalesCategoryNode = (
    node: DigitalSalesApiCategoryNode,
): CategoryInterface => ({
    id: node.id,
    code: node.code,
    label: node.label,
    children: (node.children ?? []).map(mapDigitalSalesCategoryNode),
});

export const mapDigitalSalesFilter = (
    filter: DigitalSalesApiFilter,
): FilterListInterface => ({
    id: filter.id,
    code: filter.code,
    label: filter.label,
    filterTooltip: filter.filter_tooltip,
    options: (filter.options ?? []).map((option) => ({
        id: option.id,
        code: option.code,
        label: option.label,
        sort: option.sort,
    })),
});

export const mapDigitalSalesBranding = (
    branding: DigitalSalesApiBranding,
): BrandingInterface => ({
    id: branding.id,
    name: branding.name,
    type: branding.type,
    sort: branding.sort,
    mainUrl: branding.main_url ?? "",
    thumbnailUrl: branding.thumbnail_url ?? undefined,
});
